import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSport } from "../../../../context/SportContext.tsx";
import { LeagueModel, SportModel } from "../../../../models/Sport.tsx";
import { getLiveScheduleDateRange } from "../../../../helpers/scheduleDateRange.ts";
import { GOLF_PGA_LIVE_KEY, GOLF_SPORT_NAME, isGolfFuturesLiveWindow, isPgaCommercialLiveEntry, PGA_LEAGUE_NAME } from "../../../../helpers/golfFuturesLive.ts";
import {
    isHiddenCommercialLiveLeague,
    matchPinnedCommercialLiveLeague,
    PINNED_COMMERCIAL_LIVE_LEAGUES,
    resolvePinnedCatalogLeague,
} from "../../../../helpers/commercialLivePinnedLeagues.ts";
import '../../../../styles/MenuCommercialLive.css';
import {
    FaBasketballBall,
    FaBaseballBall,
    FaHockeyPuck,
    FaFutbol,
    FaGolfBall,
    FaFootballBall,
    FaChevronLeft,
    FaChevronRight,
} from "react-icons/fa";
import { GiTennisRacket, GiBoxingGlove } from "react-icons/gi";

const iconSize = 28;
const named = (value?: string | null) => String(value ?? "").trim();

const basketballIcon = <FaBasketballBall size={iconSize} />;
const baseballIcon = <FaBaseballBall size={iconSize} />;
const footballIcon = <FaFootballBall size={iconSize} />;
const hockeyIcon = <FaHockeyPuck size={iconSize} />;
const tennisIcon = <GiTennisRacket size={iconSize} />;
const golfIcon = <FaGolfBall size={iconSize} />;
const soccerIcon = <FaFutbol size={iconSize} />;
const combatIcon = <GiBoxingGlove size={iconSize} />;

const sportIcons: Record<string, JSX.Element> = {
    basketball: basketballIcon,
    baseball: baseballIcon,
    football: footballIcon,
    hockey: hockeyIcon,
    tennis: tennisIcon,
    golf: golfIcon,
    soccer: soccerIcon,
    mma: combatIcon,
    fighting: combatIcon,
    ufc: combatIcon,
};

function resolveSportIcon(sportName?: string, sportId?: string) {
    const name = named(sportName).toLowerCase();
    const id = named(sportId).toLowerCase();
    return sportIcons[name] ?? sportIcons[id] ?? soccerIcon;
}

function resolveCatalogLeague(sportsList: SportModel[] | undefined, leagueName: string) {
    if (!leagueName || !sportsList?.length) return null;
    for (const sport of sportsList) {
        const league = sport.leagues?.find((item) => named(item.name) === leagueName);
        if (league) return { sport, league };
    }
    return null;
}

function LeagueLabel({ name }: { name: string }) {
    const containerRef = useRef<HTMLSpanElement>(null);
    const textRef = useRef<HTMLSpanElement>(null);
    const [marquee, setMarquee] = useState(false);

    useEffect(() => {
        const container = containerRef.current;
        const text = textRef.current;
        if (!container || !text) return;

        let frame = 0;
        const checkOverflow = () => {
            cancelAnimationFrame(frame);
            frame = requestAnimationFrame(() => {
                const previousWhiteSpace = text.style.whiteSpace;
                text.style.whiteSpace = "nowrap";
                const overflows = text.scrollWidth > container.clientWidth + 1;
                text.style.whiteSpace = previousWhiteSpace;
                setMarquee((prev) => (prev === overflows ? prev : overflows));
            });
        };

        checkOverflow();

        const observer = new ResizeObserver(checkOverflow);
        observer.observe(container);

        return () => {
            cancelAnimationFrame(frame);
            observer.disconnect();
        };
    }, [name]);

    return (
        <span ref={containerRef} className={`sport-label${marquee ? " sport-label--marquee" : ""}`} title={name}>
            <span className="sport-label__track">
                <span ref={textRef} className="sport-label__text">
                    {name}
                </span>
                {marquee && (
                    <span className="sport-label__text sport-label__text--clone" aria-hidden="true">
                        {name}
                    </span>
                )}
            </span>
        </span>
    );
}

type MenuLiveEntry = {
    key: string;
    league: LeagueModel | { id: string; name: string; numerical_id?: number | null };
    sport: SportModel | { id: string; name: string; numerical_id?: number | null };
    configName: string;
    order: number;
};

function isGolfLiveEntry(entry: MenuLiveEntry) {
    const sportName = named(entry.sport?.name).toLowerCase();
    const leagueName = named(entry.configName || entry.league?.name).toUpperCase();
    return sportName === GOLF_SPORT_NAME.toLowerCase() || leagueName === PGA_LEAGUE_NAME;
}

function LiveMenuSkeleton({ count = 8 }: { count?: number }) {
    return (
        <div className="sports-container" aria-hidden="true" aria-busy="true">
            {Array.from({ length: count }).map((_, index) => (
                <div key={index} className="sport-item-live sport-item-live--skeleton">
                    <span className="container-icon-live sk-live-menu-shimmer" />
                    <span className="sport-label-skeleton sk-live-menu-shimmer" />
                </div>
            ))}
        </div>
    );
}

function MenuLeague() {

    const {
        leagueLive,
        loadGames,
        checkedLeagues,
        leagueLiveconfig,
        loadConfig,
        loadLiveEvents,
        sports,
        loadSports,
        setCheckedLeagues,
        setCommercialGames,
        clearCommercialGolfFutures,
    } = useSport();
    const hasAutoOpenedRef = useRef(false);
    const scrollRef = useRef<HTMLDivElement>(null);
    const [menuReady, setMenuReady] = useState(false);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);

    const updateScrollHints = useCallback(() => {
        const el = scrollRef.current;
        if (!el) return;

        const hasOverflow = el.scrollWidth > el.clientWidth + 2;
        const nextLeft = hasOverflow && el.scrollLeft > 4;
        const nextRight = hasOverflow && el.scrollLeft < el.scrollWidth - el.clientWidth - 4;
        setCanScrollLeft((prev) => (prev === nextLeft ? prev : nextLeft));
        setCanScrollRight((prev) => (prev === nextRight ? prev : nextRight));
    }, []);

    const scrollSports = (direction: -1 | 1) => {
        const el = scrollRef.current;
        if (!el) return;
        el.scrollBy({ left: direction * 88, behavior: "smooth" });
    };

    useEffect(() => {
        let cancelled = false;

        const loadMenuSources = async () => {
            await Promise.all([
                loadConfig?.(),
                loadLiveEvents?.(),
                sports?.length ? Promise.resolve() : loadSports?.(),
            ]);
            if (!cancelled) setMenuReady(true);
        };

        loadMenuSources();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleCheckboxChange = (sport: SportModel | { name: string }, league: LeagueModel | { name: string }, marketKey?: string, markets?: string[], isMain?: boolean, startDateAfter?: string, startDateBefore?: string) => {
        if (!named(sport?.name) || !named(league?.name)) return;
        const marketArray = markets ?? [];
        loadGames(sport.name, league, marketKey, marketArray, undefined, isMain, startDateAfter, startDateBefore);
    };

    const configByName = useMemo(() => {
        const map = new Map<string, { name: string; order: number }>();
        for (const item of leagueLiveconfig ?? []) {
            const name = named(item?.name);
            if (!name) continue;
            map.set(name.toLowerCase(), { name, order: item.order ?? 999 });
        }
        return map;
    }, [leagueLiveconfig]);

    const menuEntries = useMemo((): MenuLiveEntry[] => {
        const entries: MenuLiveEntry[] = [];
        const seen = new Set<string>();
        const seenPinned = new Set<string>();
        const lives = leagueLive?.lives ?? [];

        const pushEntry = (entry: MenuLiveEntry, pinnedName?: string) => {
            if (seen.has(entry.key)) return;
            seen.add(entry.key);
            if (pinnedName) seenPinned.add(pinnedName);
            entries.push(entry);
        };

        lives.forEach((live: any) => {
            const leagueName = named(live?.league?.name);
            if (!leagueName || isHiddenCommercialLiveLeague(leagueName)) return;

            const pinned = matchPinnedCommercialLiveLeague(leagueName);
            const config = configByName.get(leagueName.toLowerCase())
                ?? (pinned ? configByName.get(pinned.name.toLowerCase()) : undefined);

            if (!pinned && (configByName.size === 0 || !config)) return;

            const catalog = pinned
                ? resolvePinnedCatalogLeague(sports, pinned) ?? resolveCatalogLeague(sports, leagueName)
                : resolveCatalogLeague(sports, leagueName);
            const sport = catalog?.sport ?? live?.sport;
            const league = catalog?.league ?? live?.league;

            if (!named(sport?.name) || !named(league?.name)) return;

            // PGA Commercial Live is injected separately Thu–Sun only.
            if (isPgaCommercialLiveEntry(sport, league)) return;

            const key = `${sport.name}-${league.name}`;
            pushEntry({
                key,
                league,
                sport,
                configName: config?.name ?? pinned?.name ?? league.name,
                order: pinned
                    ? pinned.fallbackOrder
                    : (config?.order ?? 999) + PINNED_COMMERCIAL_LIVE_LEAGUES.length,
            }, pinned?.name);
        });

        PINNED_COMMERCIAL_LIVE_LEAGUES.forEach((pinned) => {
            if (seenPinned.has(pinned.name)) return;

            const catalog = resolvePinnedCatalogLeague(sports, pinned);
            const config = configByName.get(pinned.name.toLowerCase());
            const sport = catalog?.sport ?? { id: pinned.sportId, name: pinned.sportName };
            const league = catalog?.league ?? { id: pinned.name, name: pinned.name };
            const key = `${sport.name}-${league.name}`;

            pushEntry({
                key,
                league,
                sport,
                configName: config?.name ?? pinned.name,
                order: pinned.fallbackOrder,
            }, pinned.name);
        });

        if (isGolfFuturesLiveWindow()) {
            const golfSport =
                sports?.find((s: SportModel) => s.name?.toLowerCase() === GOLF_SPORT_NAME.toLowerCase()) ??
                sports?.find((s: SportModel) => s.id?.toLowerCase() === "golf");
            const pgaLeague = golfSport?.leagues?.find(
                (l: LeagueModel) => l.name?.toUpperCase() === PGA_LEAGUE_NAME
            );
            const golfConfig = configByName.get(PGA_LEAGUE_NAME.toLowerCase());

            if (!seen.has(`${GOLF_SPORT_NAME}-${PGA_LEAGUE_NAME}`)) {
                pushEntry({
                    key: `${GOLF_SPORT_NAME}-${PGA_LEAGUE_NAME}`,
                    league: pgaLeague
                        ? { ...pgaLeague, name: PGA_LEAGUE_NAME }
                        : { id: PGA_LEAGUE_NAME, name: PGA_LEAGUE_NAME },
                    sport: golfSport
                        ? { ...golfSport, name: GOLF_SPORT_NAME }
                        : { id: "golf", name: GOLF_SPORT_NAME },
                    configName: PGA_LEAGUE_NAME,
                    order: (golfConfig?.order ?? 999) + PINNED_COMMERCIAL_LIVE_LEAGUES.length,
                });
            }
        }

        return entries.sort((a, b) => a.order - b.order);
    }, [leagueLive, sports, configByName]);

    useEffect(() => {
        if (isGolfFuturesLiveWindow()) return;
        if (!checkedLeagues?.[GOLF_PGA_LIVE_KEY]) return;

        setCheckedLeagues((prev: Record<string, boolean>) => ({ ...prev, [GOLF_PGA_LIVE_KEY]: false }));
        setCommercialGames((prev: Array<{ leagueKey?: string }>) =>
            prev.filter(g => g.leagueKey !== GOLF_PGA_LIVE_KEY)
        );
        clearCommercialGolfFutures?.();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!menuReady) return;
        if (hasAutoOpenedRef.current) return;
        if (menuEntries.length === 0) return;

        const hasAnyLiveActive = Object.entries(checkedLeagues || {}).some(
            ([k, v]) => v === true && (k.endsWith("-Live") || k.endsWith("-1stHalf"))
        );

        if (hasAnyLiveActive) {
            hasAutoOpenedRef.current = true;
            return;
        }

        const defaultEntry = menuEntries.find((entry) => !isGolfLiveEntry(entry)) ?? menuEntries.find(isGolfLiveEntry);
        if (!defaultEntry) return;

        const { startDateAfter, startDateBefore } = getLiveScheduleDateRange();
        hasAutoOpenedRef.current = true;
        handleCheckboxChange(
            defaultEntry.sport as SportModel,
            defaultEntry.league as LeagueModel,
            "Live",
            undefined,
            true,
            startDateAfter,
            startDateBefore
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [menuReady, menuEntries, checkedLeagues]);

    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;

        let frame = 0;
        const scheduleUpdate = () => {
            cancelAnimationFrame(frame);
            frame = requestAnimationFrame(updateScrollHints);
        };

        scheduleUpdate();

        const observer = new ResizeObserver(scheduleUpdate);
        observer.observe(el);
        el.addEventListener("scroll", scheduleUpdate, { passive: true });
        window.addEventListener("resize", scheduleUpdate);

        return () => {
            cancelAnimationFrame(frame);
            observer.disconnect();
            el.removeEventListener("scroll", scheduleUpdate);
            window.removeEventListener("resize", scheduleUpdate);
        };
    }, [menuReady, menuEntries, updateScrollHints]);

    return (
        <div className="live-container-menu">
            <div className="jock-sports-live-betting">
                <span className="jock-sports-live-betting-text"> JockSports Live Betting</span>
            </div>
            <div className="sports-container-wrapper">
                {menuReady && canScrollLeft && (
                    <button type="button" className="sports-scroll-hint sports-scroll-hint--left" onClick={() => scrollSports(-1)} aria-label="Previous leagues">
                        <FaChevronLeft />
                    </button>
                )}
                {menuReady ? (
                    <div className="sports-container" ref={scrollRef}>
                        {menuEntries.map((entry, indexLeague) => {
                            const { league, sport } = entry;
                            const key = `live-${sport.name}-${league.name}-${indexLeague}`;
                            const checkedKey = `${sport.name}-${league.name}-Live`;
                            const is_main = true;

                            return (
                                <div key={key} className={`sport-item-live ${checkedLeagues[checkedKey] ? "active" : ""}`}
                                    onClick={() => {
                                        const { startDateAfter, startDateBefore } = getLiveScheduleDateRange();
                                        handleCheckboxChange(
                                            sport as SportModel, league as LeagueModel, "Live",
                                            undefined, is_main,
                                            startDateAfter, startDateBefore
                                        );
                                    }}>
                                    <div className="container-icon-live">
                                        <span className="sport-icon-live">
                                            {resolveSportIcon(sport.name, sport.id)}
                                        </span>
                                    </div>
                                    <LeagueLabel name={league.name} />
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <LiveMenuSkeleton />
                )}
                {menuReady && canScrollRight && (
                    <button type="button" aria-label="Next leagues" className="sports-scroll-hint sports-scroll-hint--right" onClick={() => scrollSports(1)}>
                        <FaChevronRight />
                    </button>
                )}
            </div>
        </div>

    )
}

export default MenuLeague;
